/**
 * Платежи (ADMIN_SPEC §10, design Handoff 4). Оркестратор: журнал (мок-
 * запрос по периоду), фильтры (поиск + статус), период, детали транзакции,
 * пометка возврата и тост. Раскладка — десктоп-таблица / мобилка-список.
 */

import {useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';

import {
  markRefund,
  type PaymentRow,
  paymentsQueryOptions,
  type PaymentStatus,
} from '@services/payments';
import {keys} from '@queries/keys';
import {useBreakpoint} from '@hooks/useBreakpoint';
import {useToast} from '@hooks/useToast';
import {DEFAULT_RANGE, type Range, rangeLabel} from '@components/PeriodPicker';
import {Toast} from '@components/Toast';

import {PaymentDetailModal} from './PaymentDetailModal';
import {PaymentsDesktop} from './PaymentsDesktop';
import {PaymentsMobile} from './PaymentsMobile';
import {RefundModal} from './RefundModal';

export const Payments = () => {
  const {isMobile} = useBreakpoint();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const {toast, notify} = useToast();

  const [period, setPeriod] = useState<Range>(DEFAULT_RANGE());
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<PaymentStatus | null>(null);
  const [detail, setDetail] = useState<PaymentRow | null>(null);
  const [refunding, setRefunding] = useState<PaymentRow | null>(null);

  const {data, isLoading} = useQuery(paymentsQueryOptions(period.from, period.to));
  const refundMut = useMutation({mutationFn: markRefund});

  const rows = useMemo(() => {
    const list = data?.rows ?? [];
    const q = search.trim().toLowerCase();
    return list.filter(
      (r) => (!status || r.status === status) && (!q || r.client.toLowerCase().includes(q)),
    );
  }, [data, search, status]);

  const openClient = (row: PaymentRow) => {
    setDetail(null);
    navigate(`/clients?q=${encodeURIComponent(row.client)}`);
  };

  const confirmRefund = (reason: string) => {
    if (!refunding) return;
    refundMut.mutate(
      {paymentId: refunding.id, reason},
      {
        onSuccess: () => {
          qc.invalidateQueries({queryKey: keys.payments.all});
          setRefunding(null);
          notify('Возврат отмечен · запись создана', 2200);
        },
      },
    );
  };

  const layoutProps = {
    data,
    isLoading,
    rows,
    search,
    onSearch: setSearch,
    status,
    onStatus: setStatus,
    period,
    periodLabel: rangeLabel(period),
    onPeriod: setPeriod,
    onDetail: setDetail,
    onOpenClient: openClient,
    onRefund: setRefunding,
    onSoon: (text: string) => notify(text),
  };

  return (
    <>
      {isMobile ? <PaymentsMobile {...layoutProps} /> : <PaymentsDesktop {...layoutProps} />}

      <PaymentDetailModal
        payment={detail}
        mobile={isMobile}
        onClose={() => setDetail(null)}
        onOpenClient={openClient}
        onRefund={(r) => {
          setDetail(null);
          setRefunding(r);
        }}
      />
      <RefundModal
        payment={refunding}
        open={refunding !== null}
        pending={refundMut.isPending}
        mobile={isMobile}
        onOpenChange={(o) => !o && !refundMut.isPending && setRefunding(null)}
        onConfirm={confirmRefund}
      />
      <Toast message={toast} />
    </>
  );
};
